import { Compound } from 'BMapsModel';
import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsSrc/cmds';
import {
    NumberWidget, TextWidget, TextareaWidget, PocketWidget, ContentWidget,
    SubmitWidget, JobTypeSelectWidget, SelectWidget, VariantWidget, CheckboxWidget,
} from './simpleWidgets';
import TypedEntryList from './TypedEntryList';

// The registry: every widget type this form system knows how to render, and
// (for the ones that need it) how to turn its raw `formState[key]` value
// into what a job's `buildRequestPayload` actually wants. This is the one
// file that needs to know every widget exists — `FieldList.jsx` only needs
// to know `WIDGETS`/`RESOLVERS` themselves, never the individual widgets.
export const WIDGETS = {
    number: NumberWidget,
    text: TextWidget,
    // `jobName` renders identically to `text` — it's a distinct widget type
    // only so `JobForm` can find "whichever input names this job" generically
    // (by widget, the same way it finds `jobTypeSelect`), regardless of what
    // key or label a given tab gives it (e.g. protein folding's "System
    // Name" vs. compound generation's "Job Name").
    jobName: TextWidget,
    textarea: TextareaWidget,
    entryList: TypedEntryList,
    select: SelectWidget,
    checkbox: CheckboxWidget,
    variant: VariantWidget,
    pocket: PocketWidget,
    jobTypeSelect: JobTypeSelectWidget,
    content: ContentWidget,
    submit: SubmitWidget,
};

/**
 * Per-widget-type submit-time resolvers: given the raw `formState[key]`
 * value a widget produced interactively, turn it into whatever a job's
 * `buildRequestPayload` actually needs — so a job definition never has to
 * know how to turn (say) a `pocket` selection into usable structures. A
 * widget with no entry here is passed through to `buildRequestPayload`
 * unchanged. Kept here, next to the registry that defines what each widget
 * type even is, rather than in `formHelpers.js`, which only orchestrates
 * calling these generically (`deriveFormValues`, via `resolveDescriptor`).
 *
 * `pocket` resolves a resSpec into the pair every pocket-consuming job
 * actually wants — the protein's PDB text and an SDF of the reference
 * ligand itself — mirroring how `DockingReference` elsewhere in the app
 * already treats a pocket reference as inherently "a protein + a ligand".
 */
export const RESOLVERS = {
    pocket: async (resSpec) => {
        const compound = resSpec && App.Workspace.getCompoundBySpec(resSpec);
        if (!compound) {
            return { error: 'Select a reference ligand to define the binding pocket.' };
        }
        const { pdb, error: pdbError } = await UserActions.GetProteinPdb(compound);
        if (pdbError || !pdb) {
            return { error: `Could not get protein structure: ${pdbError || 'no PDB produced'}` };
        }
        return { value: { proteinPdb: pdb, ligandSdf: Compound.makeSDF([compound], {}) } };
    },
};
